import { useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { CheckCircle2, MessageCircle } from "lucide-react";

export const WHATSAPP_HELP = "https://wa.me/919690181181";

/** Extract a YouTube video id from watch / youtu.be / shorts / embed URLs. */
export function ytId(url = "") {
  const m = url.match(/(?:youtube\.com\/(?:watch\?.*v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
}

/** Best thumbnail for a video card — admin-set, else YouTube auto-thumbnail. */
export function videoThumb(video) {
  if (video?.thumbnail) return video.thumbnail;
  const id = video?.provider === "youtube" ? ytId(video.url) : null;
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

function embedSrc(video) {
  if (video.provider === "youtube") {
    const id = ytId(video.url);
    return id ? `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&autoplay=1` : video.url;
  }
  if (video.provider === "vimeo") {
    const m = video.url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? `https://player.vimeo.com/video/${m[1]}?autoplay=1` : video.url;
  }
  return video.url; // direct file (Firebase Storage etc.)
}

/**
 * Embedded in-app video player dialog with watch-progress tracking.
 * Opening marks "partial"; file videos auto-complete at 90%; iframe
 * providers complete via the "Mark as Completed" button.
 */
export default function HelpVideoPlayer({ video, open, onOpenChange, onProgress }) {
  const { t, lang } = useT();
  const vidRef = useRef(null);

  const report = (body) =>
    api.post(`/help/videos/${video.video_id}/progress`, body)
      .then((r) => onProgress && onProgress(video.video_id, r.data.status))
      .catch(() => {});

  // Latest-callback ref so the effect fires only on open-transitions while
  // always seeing the freshest video/progress values.
  const onOpenReportRef = useRef(() => {});
  onOpenReportRef.current = () => {
    if (video?.progress !== "completed") report({ position_sec: 1, duration_sec: 0 });
  };
  useEffect(() => {
    if (open) onOpenReportRef.current();
  }, [open]);

  if (!video) return null;
  const title = lang === "en" ? (video.title_en || video.title_hi) : (video.title_hi || video.title_en);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="help-video-player-dialog">
        <DialogHeader className="px-5 pt-4 pb-0">
          <DialogTitle className="font-display pr-8">{title}</DialogTitle>
          {video.description && <DialogDescription>{video.description}</DialogDescription>}
        </DialogHeader>
        <div className="aspect-video bg-black">
          {video.provider === "file" ? (
            <video
              ref={vidRef}
              src={embedSrc(video)}
              controls
              autoPlay
              playsInline
              preload="metadata"
              className="w-full h-full"
              onEnded={(e) => report({ position_sec: e.target.duration || 1, duration_sec: e.target.duration || 1, completed: true })}
              onTimeUpdate={(e) => {
                const el = e.target;
                if (el.duration && el.currentTime / el.duration >= 0.9 && !el._amDone) {
                  el._amDone = true;
                  report({ position_sec: el.currentTime, duration_sec: el.duration });
                }
              }}
            />
          ) : (
            <iframe
              title={title}
              src={embedSrc(video)}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 px-5 pb-4">
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            data-testid="help-mark-completed-btn"
            onClick={() => report({ position_sec: 1, duration_sec: 1, completed: true })}
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> {t("help.markCompleted")}
          </Button>
          <a href={WHATSAPP_HELP} target="_blank" rel="noreferrer" className="ml-auto">
            <Button size="sm" className="am-btn-primary rounded-full" data-testid="help-player-whatsapp-btn">
              <MessageCircle className="w-4 h-4 mr-1.5" /> {t("help.needHelp")}
            </Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
